import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

export const InvitationCodeStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onPrevious,
}) => {
  const { t } = useLanguage();
  const [invitationCode, setInvitationCode] = useState(data.invitationCode || '');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvitationCode(e.target.value);
    setValidationError(null);
  };

  const handleValidateCode = async () => {
    if (!invitationCode.trim()) {
      setValidationError('Please enter an invitation code');
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      // TODO: Implement actual invitation code validation API call
      // For now, simulate validation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate validation result
      if (invitationCode.length < 8) {
        setValidationError('Invalid invitation code. Please check and try again.');
        return;
      }

      // Code is valid, proceed to next step
      onNext({ invitationCode });
    } catch (error) {
      setValidationError('Failed to validate invitation code. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const canGoNext = invitationCode.trim().length > 0 && !validationError;

  return (
    <OnboardingStep
      title={t('onboarding.invitationCode.title')}
      description={t('onboarding.invitationCode.description')}
      onNext={handleValidateCode}
      onPrevious={onPrevious}
      canGoNext={canGoNext}
      showNext={true}
      showPrevious={true}
      isLoading={isValidating}
    >
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('onboarding.invitationCode.enterCode')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('onboarding.invitationCode.enterCodeDescription')}
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="space-y-4">
            <div>
              <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-700 mb-2">
                {t('onboarding.invitationCode.codeLabel')}
              </label>
              <input
                type="text"
                id="invitationCode"
                value={invitationCode}
                onChange={handleCodeChange}
                placeholder={t('onboarding.invitationCode.codePlaceholder')}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationError ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isValidating}
              />
              {validationError && (
                <p className="mt-2 text-sm text-red-600">{validationError}</p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {t('onboarding.invitationCode.helpTitle')}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>{t('onboarding.invitationCode.helpDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingStep>
  );
};
